import { useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';

import { isPoweruser } from 'js/lib/user';

import SelectPermissionsUnderDomain from 'bundles/unified-home-common/queries/SelectPermissionsUnderDomain.graphql';
import type {
  SelectPermissionsUnderDomainQuery,
  SelectPermissionsUnderDomainQueryVariables,
} from 'bundles/unified-home-common/queries/__generated__/SelectPermissionsUnderDomain';

type Options = {
  thirdPartyOrganizationSlug?: string;
  userId?: number;
};

/**
 * Whether the user can preview the org as an admin: power users, org admins, and admins of any
 * program in the org.
 */
const useCanViewProgramInOrg = ({ thirdPartyOrganizationSlug, userId }: Options) => {
  const isPowerUser = isPoweruser();

  const { data, loading } = useQuery<SelectPermissionsUnderDomainQuery, SelectPermissionsUnderDomainQueryVariables>(
    SelectPermissionsUnderDomain,
    {
      variables: {
        thirdPartyOrganizationSlug: thirdPartyOrganizationSlug ?? '',
        permissions: [
          { domain: 'PERMISSION_DOMAIN_ORGANIZATION', verb: 'VIEW' },
          { domain: 'PERMISSION_DOMAIN_PROGRAM', verb: 'VIEW' },
        ],
        userId: userId ?? 0,
      },
      skip: isPowerUser || !thirdPartyOrganizationSlug || !userId,
      onError: (errorObj) => {
        captureException(errorObj, {
          extra: { message: 'Error fetching org view permissions in useCanViewProgramInOrg' },
        });
      },
    }
  );

  const hasViewPermission = !!data?.EnterprisePermission?.querySelectPermissionsUnderDomain?.some(
    (selectPermissionsUnderDomain) => selectPermissionsUnderDomain.permissions.length > 0
  );

  return {
    canViewProgramInOrg: isPowerUser || hasViewPermission,
    isLoading: loading,
  };
};

export default useCanViewProgramInOrg;
